import React, { useEffect, useState, useRef } from "react";
import {
	postCategoryServices,
	postService,
	taxonomyServices,
	templateServices,
} from "api/services";
import { IResPostCategory } from "api/services/postCategories/types";
import { useParams } from "react-router-dom";
import {
	Grid,
	Paper,
	TextField,
	Typography,
	FormControl,
	Input,
	InputLabel,
	Select,
	Button,
	MenuItem,
} from "@material-ui/core";
import Loader from "react-loader-spinner";
import handleNotification from "helpers/handleNotification";
import { FileUploader } from "shared/FileUploader/FileUploader";
import { MainTemplate } from "shared/MainTemplate/MainTemplate";
import { IPost } from "api/services/posts/types";
import getImageByImageSize from "helpers/getImageByImageSize";
import { IFile } from "api/services/file/types";
import Form from "shared/Form/Form";
import { transliterate } from "helpers/transliterate";
import { IResTemplate } from "api/services/templates/types";
import { CLIENT_URL } from "constants/project";
import { IResTaxonomy } from "api/services/taxonomies/types";
import { useSelector } from "react-redux";
import { AppState } from "store/store";
import { ISeoPayload, seoFields, seoView } from "constants/seo";
import "./UpdatePostPage.sass";
import { UserRoles } from "api/services/user/types";

export const UpdatePostPage: React.FC = () => {
	const { taxonomyId, postId } =
		useParams<{ taxonomyId: string; postId: string }>();
	const { user } = useSelector((state: AppState) => state.userReducer);
	const { currentLang } = useSelector((state: AppState) => state.langReducer);
	const [title, setTitle] = useState("");
	const [shortDescription, setShortDescription] = useState("");
	const [categories, setCategories] = useState<IResPostCategory[]>();
	const [categoryId, setCategoryId] = useState<number>();
	const [file, setFile] = useState<IFile>();
	const [slug, setSlug] = useState("");
	const [taxonomyName, setTaxonomyName] = useState("");
	const [categoryName, setCategoryName] = useState("");
	const [loading, setLoading] = useState(false);
	const [hasError, setHasError] = useState(false);
	const formRef = useRef<any>();
	const seoRef = useRef<any>();
	const [activeTemplate, setActiveTemplate] = useState<IResTemplate>();
	const [templateId, setTemplateId] = useState<number>();
	const [templates, setTemplates] = useState<IResTemplate[]>();
	const [data, setData] = useState<IAnyObject>();
	const [seo, setSeo] = useState<ISeoPayload>();
	const [heightImage, setHeightImage] = useState<number>();
	const typeNews = Number(taxonomyId) === 1 || undefined;

	useEffect(() => {
		if (currentLang?.code) {
			getPostById();
			getCategories();
			getTaxonomy();
			getTemplates();
		}
	}, [currentLang?.code]);

	useEffect(() => {
		const template = templates?.find((i) => i.id === templateId);
		if (template) {
			setActiveTemplate(template);
		}
	}, [templateId, templates?.length]);

	const getTemplates = async () => {
		const query = { taxonomyId };
		const res = await templateServices
			.getTemplates(query)
			.then((res) => res.data);
		setTemplates(res);
	};

	const getTaxonomy = async () => {
		const res = await taxonomyServices.getTaxonomies().then((res) => res.data);
		res?.forEach((i: IResTaxonomy) => {
			if (i.id === Number(taxonomyId)) {
				setTaxonomyName(`/${i.name}`);
			}
		});
	};

	const getPostById = async () => {
		const res = await postService.getPostById(postId).then((res) => res.data);
		const {
			title,
			slug,
			file,
			categoryId,
			data,
			templateId,
			seoDescription,
			seoKeywords,
			seoTitle,
			seoOgImage,
			category,
			shortDescription,
		} = res;
		setCategoryName(category.name);
		setTitle(title);
		setSlug(slug || "");
		setFile(file);
		setCategoryId(categoryId);
		setData(data);
		setTemplateId(templateId);
		setShortDescription(shortDescription || "");
		setSeo({
			description: seoDescription,
			keywords: seoKeywords,
			title: seoTitle,
			ogImage: seoOgImage,
		});
	};

	const getCategories = async () => {
		const res = await postCategoryServices
			.getPostCategories({ taxonomyId })
			.then((res) => res.data);
		setCategories(res);
	};
	const updatePost = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!title.trim() || !templateId) {
			handleNotification("Заполните поля");
			setHasError(true);
			return;
		}
		const formRes = formRef.current?.submit();
		if (!formRes || (formRes?.hasErrors && user?.role !== UserRoles.SU)) {
			handleNotification("Заполните поля");
			return;
		}
		const seoRes = seoRef.current?.submit();

		const requestData: IPost = {
			title,
			categoryId: Number(categoryId),
			fileId: file?.id,
			slug,
			data: formRes?.res ?? {},
			templateId,
			seoTitle: seoRes.res.title,
			seoDescription: seoRes.res.description,
			seoKeywords: seoRes.res.keywords,
			seoOgImage: seoRes.res.ogImage,
			shortDescription,
		};
		setLoading(true);
		try {
			await postService.updatePost(postId, requestData);
			handleNotification("Успешно сохранено", "success");
			getPostById();
		} finally {
			setLoading(false);
			setHasError(false);
		}
	};

	const updateTitle = (value: string) => {
		setTitle(value);
		setSlug(transliterate(value));
	};

	const setCategory = (id: number) => {
		setCategoryId(id);
		const found = categories?.find((i) => i.id === id);
		if (found) {
			setCategoryName(`/${found.name}/`);
		}
	};

	return (
		<MainTemplate title={`Редактирование поста - ${title}`} hideBtn>
			<Grid container alignItems='flex-start'>
				<Grid item xs={8} className='update-post-page__main'>
					<Grid item xs={12} className='update-post-page__container'>
						<FormControl className='update-post-page__main-title'>
							<Input
								className='update-post-page__main-title-input'
								value={title}
								autoFocus
								placeholder='Название'
								onChange={(e) => updateTitle(e.target.value as string)}
							/>
						</FormControl>
					</Grid>
					{activeTemplate && (
						<Form
							ref={formRef}
							view={activeTemplate.view}
							fields={activeTemplate.properties}
							initialData={data}
						/>
					)}
					<Grid item xs={12} className='update-post-page__seo'>
						<Form
							ref={seoRef}
							view={seoView}
							fields={seoFields}
							initialData={seo}
						/>
					</Grid>
				</Grid>
				<Grid
					item
					xs={4}
					container
					spacing={2}
					className='update-post-page__aside'
				>
					<Grid item xs={12}>
						<TextField
							variant='outlined'
							label='Ссылка'
							className='form-field'
							value={slug}
							onChange={(e) => setSlug(transliterate(e.target.value as string))}
						/>
					</Grid>
					<Grid item xs={12}>
						<a
							href={`${CLIENT_URL}/${taxonomyName}/${categoryName}/${
								categoryName.trim() ? slug : ""
							}`}
							className='update-page__aside-link'
							target='_blank'
							rel='noreferrer'
						>
							{`${CLIENT_URL}${taxonomyName}/${categoryName}/${
								categoryName.trim() ? slug : ""
							}`}
						</a>
					</Grid>
					<Grid item xs={12}>
						<FormControl
							variant='outlined'
							error={!categoryId && hasError}
							className='form-field'
						>
							<InputLabel id='categories'>Категория</InputLabel>
							<Select
								label='Категория'
								value={categoryId || ""}
								onChange={(e) => setCategory(e.target.value as number)}
							>
								{categories?.map((i) => (
									<MenuItem key={i.id} value={i.id}>
										{i.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12}>
						<FormControl
							variant='outlined'
							className='form-field'
							error={!templateId && hasError}
						>
							<InputLabel id='categories'>Шаблон</InputLabel>
							<Select
								label='Шаблон'
								value={templateId || ""}
								onChange={(e) => setTemplateId(e.target.value as number)}
							>
								{templates?.map((i) => (
									<MenuItem key={i.id} value={i.id}>
										{i.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					{typeNews && (
						<Grid item xs={12}>
							<FormControl
								variant='outlined'
								fullWidth
								className='form-field'
								error={typeNews && !!heightImage && hasError}
							>
								<InputLabel id='categories'>
									Выберите размер изображения
								</InputLabel>
								<Select
									label='Выберите размер изображения'
									value={(heightImage as number) || ""}
									onChange={(e) => setHeightImage(e.target.value as number)}
								>
									{[
										{
											title: "Большая картинка",
											height: 500,
										},
										{ title: "Маленькая картинка", height: 370 },
									]?.map((i) => {
										return (
											<MenuItem key={i.height} value={i.height}>
												{i.title}
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</Grid>
					)}
					{((typeNews && heightImage) || !typeNews) && (
						<Grid item xs={12}>
							<Paper variant='outlined'>
								<Grid container spacing={3} alignItems='stretch'>
									<Grid item xs={7} className='post-page__block'>
										<Typography
											variant='h4'
											style={{ marginBottom: "20px" }}
											className='post-page__block-title'
										>
											Медиа
										</Typography>
										{typeNews ? (
											<FileUploader
												text='Выбрать файл'
												width={400}
												height={heightImage}
												selectFile={setFile}
											/>
										) : (
											<FileUploader text='Выбрать файл' selectFile={setFile} />
										)}
									</Grid>
									<Grid
										className='post-page__media'
										container
										item
										alignItems='center'
										xs={5}
									>
										{file ? (
											<img
												className='post-page__media-item'
												src={getImageByImageSize({
													file,
													imageSize: "200x200",
												})}
												alt=''
											/>
										) : (
											<Typography className='media-item-title' variant='h6'>
												Изображение не выбрано
											</Typography>
										)}
									</Grid>
								</Grid>
							</Paper>
						</Grid>
					)}
					<Grid item xs={12} justifyContent='flex-end' container>
						<Button
							className='post-page__button'
							size='large'
							color='primary'
							variant='contained'
							onClick={updatePost}
							disabled={loading}
						>
							{loading ? (
								<Loader height={10} type='ThreeDots' color='#fff' />
							) : (
								"Сохранить"
							)}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</MainTemplate>
	);
};
